<template>
  <BaseWidget
    title="Best practices automated"
    :show-info-icon="true"
    info-tooltip="Automations ranked by best practice coverage, proof count, and priority."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/9500741-what-is-an-automation', text: 'Learn about Automations' }"
    :automations-count="false"
    :disable-date-range="true"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
      :class="{ 'focused': focused }"
    >
      <div
        v-if="focused"
        class="focused-kpi"
      >
        <span class="focused-count">{{ coveredCount }}</span>
        <div class="focused-label-row">
          <span class="focused-label">of {{ totalCount }} best practices covered</span>
          <a
            v-if="rankedAutomations.length > 0"
            class="focused-view-all-link"
            @click.prevent="handleViewAllClick"
          >
            View all
          </a>
        </div>
      </div>
      <div
        v-else
        class="automations-container"
      >
        <div
          v-if="rankedAutomations.length === 0"
          class="no-data"
        >
          No best practice automations found
        </div>
        <template v-else>
          <div class="kpi-row">
            <span class="kpi-count">{{ coveredCount }}</span>
          </div>
          <div class="period-row">
            <span class="period-label">of {{ totalCount }}</span>
            <a
              class="focused-view-all-link"
              @click.prevent="handleViewAllClick"
            >
              View all
            </a>
          </div>
          <div class="automations-list">
            <div
              v-for="automation in displayAutomations"
              :key="automation.type"
              class="automation-row"
              @click="openBestPracticeDetail(automation)"
            >
              <span
                class="automation-bolt-badge"
                :class="{
                  'automation-bolt-badge--green': automation.state === 'covered',
                  'automation-bolt-badge--yellow': automation.state === 'missed',
                  'automation-bolt-badge--grey': automation.state === 'noCoverage'
                }"
              >
                <span class="material-icons-outlined">bolt</span>
              </span>
              <div class="automation-info">
                <span class="automation-name">{{ automation.label }}</span>
                <span class="automation-meta">{{ automation.proofText }}</span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, onUnmounted } from 'vue'
import BaseWidget from './BaseWidget.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useBestPracticesData } from '../composables/useBestPracticesData.js'
import { BEST_PRACTICE_COPY, formatBestPracticeCopy } from '../utils/widgetBestPractices.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import { pushAdminSidePanel } from '../composables/useOptixPanel.js'

const props = defineProps({
  editMode: {
    type: Boolean,
    default: false
  },
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

defineEmits(['update:props', 'remove-widget'])

const { bestPractices, loading, error } = useBestPracticesData()

// Rank automations: covered (green) first, then missed (yellow), then noCoverage (grey)
const rankedAutomations = computed(() => {
  if (!bestPractices.value?.length) return []

  return bestPractices.value
    .filter(bp => BEST_PRACTICE_COPY[bp.type] && bp.type !== 'ROUTINE_COMMUNICATION')
    .map(bp => {
      const copy = BEST_PRACTICE_COPY[bp.type]
      const score = (bp.proof_count || 0) * (bp.priority || 0)

      let state, proofText
      if (bp.is_covered) {
        state = 'covered'
        proofText = copy?.covered
          ? formatBestPracticeCopy(copy.covered, bp.proof_count, copy.maxCount)
          : `${bp.proof_count || 0} actions completed`
      } else if (bp.missed_opportunity) {
        state = 'missed'
        proofText = copy?.missedShort
          ? formatBestPracticeCopy(copy.missedShort, bp.opportunity_count, copy.maxCount)
          : 'Missed opportunity'
      } else {
        state = 'noCoverage'
        proofText = copy?.noCoverage || 'Not yet automated'
      }

      return {
        type: bp.type,
        label: copy?.label || bp.type,
        isCovered: bp.is_covered,
        state,
        proofCount: bp.proof_count || 0,
        priority: bp.priority || 0,
        score,
        proofText,
        workflowId: bp.covering_workflow?.workflow_id || null
      }
    })
    .sort((a, b) => {
      const stateOrder = { covered: 0, missed: 1, noCoverage: 2 }
      const stateDiff = stateOrder[a.state] - stateOrder[b.state]
      if (stateDiff !== 0) return stateDiff
      return b.score - a.score || b.priority - a.priority
    })
})

const displayAutomations = computed(() => {
  if (props.focused) return rankedAutomations.value
  return rankedAutomations.value.slice(0, 3)
})

const coveredCount = computed(() => {
  if (!bestPractices.value?.length) return 0
  return bestPractices.value.filter(bp => bp.is_covered && BEST_PRACTICE_COPY[bp.type] && bp.type !== 'ROUTINE_COMMUNICATION').length
})

const totalCount = computed(() => {
  if (!bestPractices.value?.length) return 0
  return bestPractices.value.filter(bp => BEST_PRACTICE_COPY[bp.type] && bp.type !== 'ROUTINE_COMMUNICATION').length
})

const openBestPracticeDetail = (automation) => {
  pushAdminSidePanel('widget-detail', {
    title: automation.label,
    bestPracticeType: automation.type
  })
}

const handleViewAllClick = () => {
  pushAdminSidePanel('view-all-best-practices')
}

onUnmounted(() => {
  // No cleanup needed - bestPracticesData is a singleton
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.widget-content-wrapper.focused {
  position: relative;
}

.focused-view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.focused-view-all-link:hover {
  color: #2563eb;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.no-data {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.automations-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 4px 0 0 0;
}

.kpi-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-shrink: 0;
}

.kpi-count {
  font-size: 28px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.15;
}

.kpi-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
}

.period-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  margin-top: 2px;
  margin-bottom: 8px;
}

.period-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.automations-list {
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 0 0 auto;
}

.automation-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 64px;
  flex-shrink: 0;
  padding: 10px 4px;
  box-sizing: border-box;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
}

.automation-row:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.automation-row:last-child {
  border-bottom: none;
}

.automation-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.automation-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.automation-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.automation-bolt-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  flex-shrink: 0;
}

.automation-bolt-badge .material-icons-outlined {
  font-size: 14px;
}

.automation-bolt-badge--green {
  background: var(--color-bg-green);
}

.automation-bolt-badge--green .material-icons-outlined {
  color: #1C3829;
}

.automation-bolt-badge--yellow {
  background: var(--color-bg-yellow);
}

.automation-bolt-badge--yellow .material-icons-outlined {
  color: var(--color-yellow-text);
}

.automation-bolt-badge--grey {
  background: rgba(241, 240, 239, 1);
}

.automation-bolt-badge--grey .material-icons-outlined {
  color: #6b7280;
}

.automation-meta {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.focused-kpi {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.focused-count {
  font-size: 28px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.15;
}

.focused-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.focused-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}
</style>
